import { useEffect, useState, useCallback } from "react";

/**
 * useAdminPWA — admin-scoped PWA lifecycle hook.
 *
 * - Registers the admin service worker (only when pathname starts with /admin).
 * - Captures the `beforeinstallprompt` event so the UI can surface an install button.
 * - Detects standalone / installed mode so the UI can hide install affordances.
 * - Exposes `promptInstall()` and `updateAvailable` flag.
 *
 * Usage:
 *   const { canInstall, isStandalone, promptInstall, updateAvailable, applyUpdate } = useAdminPWA();
 */
export function useAdminPWA() {
    const [canInstall, setCanInstall] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [installEvent, setInstallEvent] = useState(null);
    const [registration, setRegistration] = useState(null);

    // Detect standalone / installed.
    useEffect(() => {
        const mql = window.matchMedia("(display-mode: standalone)");
        const update = () =>
            setIsStandalone(mql.matches || window.navigator.standalone === true);
        update();
        mql.addEventListener?.("change", update);
        return () => mql.removeEventListener?.("change", update);
    }, []);

    // Register service worker (admin scope only).
    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;
        if (!window.location.pathname.startsWith("/admin")) return;

        let cancelled = false;
        navigator.serviceWorker
            .register("/admin-sw.js", { scope: "/admin" })
            .then((reg) => {
                if (cancelled) return;
                setRegistration(reg);
                // Track updates found after initial load.
                reg.addEventListener("updatefound", () => {
                    const nw = reg.installing;
                    if (!nw) return;
                    nw.addEventListener("statechange", () => {
                        if (nw.state === "installed" && navigator.serviceWorker.controller) {
                            setUpdateAvailable(true);
                        }
                    });
                });
            })
            .catch((err) => {
                console.debug("[admin-pwa] SW registration failed:", err?.message || err);
            });
        return () => { cancelled = true; };
    }, []);

    // beforeinstallprompt → enable the install button.
    useEffect(() => {
        const onBip = (e) => {
            e.preventDefault();
            setInstallEvent(e);
            setCanInstall(true);
        };
        const onInstalled = () => {
            setCanInstall(false);
            setInstallEvent(null);
        };
        window.addEventListener("beforeinstallprompt", onBip);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBip);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!installEvent) return { outcome: "dismissed" };
        await installEvent.prompt();
        const choice = await installEvent.userChoice;
        if (choice?.outcome === "accepted") {
            setCanInstall(false);
            setInstallEvent(null);
        }
        return choice;
    }, [installEvent]);

    const applyUpdate = useCallback(() => {
        if (!registration?.waiting) {
            window.location.reload();
            return;
        }
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        // Reload once the new SW takes control.
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            window.location.reload();
        });
    }, [registration]);

    return { canInstall, isStandalone, promptInstall, updateAvailable, applyUpdate };
}
